import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTAPair } from "../components/CTA";
import { Reveal } from "../components/Motion";
import { getService, SERVICES } from "../data/services";
import NotFound from "./NotFound";

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const service = getService(serviceSlug);
  if (!service) return <NotFound />;
  const idx = SERVICES.findIndex((s) => s.slug === service.slug);
  const related = SERVICES.filter((_, i) => i !== idx).slice(Math.max(0, idx - 1), Math.max(0, idx - 1) + 3);

  return (
    <main data-testid="service-detail-page">
      <SEO title={service.meta.title} description={service.meta.description} />
      <section className="bg-paper grain relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-14 md:pt-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-steel font-semibold mb-4" data-testid="service-audience">
                Service {String(idx + 1).padStart(2, "0")} · {service.audience}
              </p>
              <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="service-title">{service.name}</h1>
              <p className="mt-6 max-w-2xl text-lg text-smoke leading-relaxed" data-testid="service-plain">{service.plain}</p>
              <div className="mt-8"><CTAPair service={service.name} testidPrefix="service-cta" /></div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-rust pointer-events-none" aria-hidden />
              <img src={service.image} alt={`${service.name} — mobile welding repair in Fort Worth, TX`} data-testid="service-image" className="relative w-full aspect-[4/3] object-cover" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-warmline py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12">
          <Reveal>
            <div data-testid="service-who">
              <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl">Who calls us for this</h2>
              <ul className="mt-6 space-y-3.5">
                {service.whoNeeds.map((w) => (
                  <li key={w} className="flex gap-3 text-smoke leading-relaxed">
                    <CheckCircle2 size={20} className="text-rust shrink-0 mt-0.5" aria-hidden />{w}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div data-testid="service-materials">
              <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl">Materials we weld</h2>
              <ul className="mt-6 space-y-3.5">
                {service.materials.map((m) => (
                  <li key={m} className="flex gap-3 text-smoke leading-relaxed">
                    <span className="w-2 h-2 bg-steel rotate-45 shrink-0 mt-2.5" aria-hidden />{m}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl mb-8" data-testid="service-expect-heading">What to expect during the visit</h2>
          </Reveal>
          <ol className="grid sm:grid-cols-2 gap-px bg-warmline border border-warmline" data-testid="service-expect">
            {service.expect.map((step, i) => (
              <li key={i} className="bg-cream p-7 flex gap-5">
                <span className="font-display font-black text-3xl text-rust/25 shrink-0">{i + 1}</span>
                <p className="text-smoke leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="cut-both bg-charcoal text-cream py-16 relative z-[5] -my-[1px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-6">
          <h2 className="font-display font-black tracking-tight text-2xl sm:text-4xl" data-testid="service-cta-heading">
            Send two photos. Get a straight price.
          </h2>
          <CTAPair service={service.name} dark testidPrefix="service-band-cta" />
        </div>
      </section>

      <section className="bg-paper py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-display font-extrabold tracking-tight text-xl mb-6">Related repairs</h2>
          <div className="flex flex-wrap gap-3" data-testid="service-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-${r.slug}`}
                className="btn-cut inline-flex items-center gap-2 border-2 border-ink/60 px-4 py-2 text-sm font-bold hover:bg-charcoal hover:text-cream hover:border-charcoal transition-colors">
                {r.name} <ArrowRight size={14} aria-hidden />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;
